import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Keyboard, type KeyboardInfo } from "@capacitor/keyboard";
import { useAppSettings } from "../components/providers/AppSettingsProvider";
import { loadAuthUser, saveAuthUser, type AuthUser } from "../services/auth";
import { getOnboardingData, saveOnboardingData } from "../utils";
import { getPageBackgroundGradient } from "../config/theme";
import { Pencil, ArrowLeft } from "lucide-react";

const EditAccount: React.FC = () => {
  const { t, theme } = useAppSettings();
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [profilePhoto, setProfilePhoto] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const dismissKeyboard = async () => {
    // Blur focused input (works on web + native WebView)
    (document.activeElement as HTMLElement | null)?.blur?.();
    // On native platforms, ask Capacitor to hide keyboard
    try {
      await Keyboard.hide();
    } catch {
      // no-op (e.g. web)
    }
  };

  useEffect(() => {
    const loadedUser = loadAuthUser();
    if (!loadedUser) {
      navigate("/profile");
      return;
    }
    setUser(loadedUser);
    setName(loadedUser.full_name || "");
    setUsername(loadedUser.username || loadedUser.email?.split("@")[0] || "");
    setEmail(loadedUser.email || "");
    setProfilePhoto(loadedUser.profile_photo_url);
  }, [navigate]);

  useEffect(() => {
    // Ensure focused inputs are not covered by the native keyboard (iOS/Android)
    let willShowPromise: Promise<{ remove: () => void }> | undefined;
    let willHidePromise: Promise<{ remove: () => void }> | undefined;

    try {
      willShowPromise = Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
        setKeyboardHeight(info.keyboardHeight || 0);
      });
      willHidePromise = Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0);
      });
    } catch {
      // no-op (e.g. web)
    }

    return () => {
      try {
        willShowPromise?.then((h) => h.remove()).catch(() => { });
        willHidePromise?.then((h) => h.remove()).catch(() => { });
      } catch {
        // no-op
      }
    };
  }, []);

  const scrollFieldIntoView = (el: HTMLElement | null) => {
    if (!el) return;
    // Let keyboard animation/layout settle first
    window.setTimeout(() => {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch {
        // Older webviews might not support options
        try {
          el.scrollIntoView();
        } catch {
          // no-op
        }
      }
    }, 250);
  };

  const handleSave = async () => {
    if (!user) return;
    await dismissKeyboard();

    const updatedUser: AuthUser = {
      ...user,
      username: username || user.username,
      full_name: name,
      email: email,
      profile_photo_url: profilePhoto || user.profile_photo_url,
    };

    saveAuthUser(updatedUser);
    // Keep name consistent across the app: getUserDisplayName() prioritizes onboarding name.
    try {
      const existing = getOnboardingData() || {};
      const normalizedName = (name || "").trim();
      saveOnboardingData({
        ...existing,
        ...(normalizedName ? { name: normalizedName } : {}),
      });
    } catch {
      // no-op
    }
    alert(t('profile.editAccount.saved'));
    navigate("/profile");
  };

  const handleCancel = async () => {
    await dismissKeyboard();
    navigate("/profile");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setProfilePhoto(result);
      setUser(prev => prev ? { ...prev, profile_photo_url: result } : prev);
    };
    reader.readAsDataURL(file);
  };

  const handlePickImage = () => {
    // 触发系统相册/相机选择，权限由系统弹窗处理
    fileInputRef.current?.click();
  };

  const getInitial = () => {
    if (name) {
      return name.charAt(0).toUpperCase();
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  if (!user) {
    return null;
  }

  const animation = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <div className="fixed inset-0 z-[60] w-full h-screen overflow-hidden">
      {/* 固定背景层 */}
      <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />

      {/* 可滚动内容层 */}
      <div
        className="relative z-10 text-white px-6 pt-16 h-full overflow-y-auto flex flex-col"
        style={{ paddingBottom: 32 + keyboardHeight }}
      >
        <div className="max-w-md mx-auto w-full flex-1 flex flex-col justify-center space-y-8">
          {/* 返回按钮 - 暂时隐藏 */}
          {false && (
            <motion.div {...animation} transition={{ delay: 0.1 }} className="absolute top-16 left-6">
              <button
                onClick={() => navigate("/profile")}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
            </motion.div>
          )}

          {/* 标题 */}
          <motion.div {...animation} transition={{ delay: 0.1 }} className="text-center">
            <h1 className="text-2xl font-medium text-white">{t('profile.editAccount')}</h1>
          </motion.div>

          {/* 头像 */}
          <motion.div {...animation} transition={{ delay: 0.2 }} className="flex justify-center">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-blue-500 flex items-center justify-center">
                {(profilePhoto || user.profile_photo_url) ? (
                  <img
                    src={profilePhoto || user.profile_photo_url}
                    alt={t('profile.altProfilePhoto')}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span className="text-4xl font-medium text-white">{getInitial()}</span>
                )}
              </div>
              {/* 编辑图标 */}
              <button
                type="button"
                onClick={handlePickImage}
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center border-2 border-white"
              >
                <Pencil className="w-4 h-4 text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </motion.div>

          {/* 输入字段 */}
          <motion.div {...animation} transition={{ delay: 0.3 }} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm text-white/70 mb-2">{t('profile.editAccount.name')}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  const value = e.target.value;
                  // 限制最大长度为30个字符
                  if (value.length <= 30) {
                    setName(value);
                  }
                }}
                onFocus={(e) => scrollFieldIntoView(e.currentTarget)}
                className="w-full bg-transparent text-white text-lg border-0 border-b border-white/30 focus:outline-none focus:border-white/60 pb-2 transition-colors"
                placeholder={t('profile.editAccount.name')}
                maxLength={30}
              />
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm text-white/70 mb-2">{t('profile.editAccount.username')}</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onFocus={(e) => scrollFieldIntoView(e.currentTarget)}
                className="w-full bg-transparent text-white text-lg border-0 border-b border-white/30 focus:outline-none focus:border-white/60 pb-2 transition-colors"
                placeholder={t('profile.editAccount.username')}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm text-white/70 mb-2">{t('profile.editAccount.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={(e) => scrollFieldIntoView(e.currentTarget)}
                className="w-full bg-transparent text-white text-lg border-0 border-b border-white/30 focus:outline-none focus:border-white/60 pb-2 transition-colors"
                placeholder={t('profile.editAccount.email')}
              />
            </div>
          </motion.div>

          {/* 操作按钮 */}
          <motion.div {...animation} transition={{ delay: 0.4 }} className="pt-8 grid grid-cols-2 gap-3">
            <button
              onClick={handleSave}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-colors rounded-xl p-4 flex items-center justify-center gap-2 shadow-lg"
            >
              <span className="text-sm font-medium">{t('profile.editAccount.save')}</span>
            </button>
            <button
              onClick={handleCancel}
              className="w-full bg-white/10 hover:bg-white/20 transition-colors rounded-xl p-4 flex items-center justify-center gap-2 border border-white/20"
            >
              <span className="text-sm font-medium text-white">{t('common.cancel')}</span>
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EditAccount;

